import {Dispatch, FC, ReactNode, SetStateAction, useEffect, useState} from 'react'
import {AutomaticInvoiceTemplateSetting, Product, ProductCategory} from '../core/_models'
import {
  getAutomaticInvoiceRatioSummary,
  getOnlineProductServicePriceSources,
  getProductCategories,
  updateProduct,
} from '../core/_requests'
import {useQueryResponse} from '../core/QueryResponseProvider'
import {useListView} from '../core/ListViewProvider'
import {CustomInputs} from '../../../../../../_metronic/helpers/components/CustomInputs'
import {Stuffid} from '../../../stuffid-management/stuffids-list/core/_models'
import {ProductStuffIdEdit} from './ProductStuffIdEdit'
import {
  SearchSelect,
  SearchSelectOption,
} from '../../../../../../_metronic/helpers/components/SearchSelect'
import {
  API_URL,
  isNotEmpty,
  numberWithCommas,
  projectName,
} from '../../../../../../_metronic/helpers'
import axios from 'axios'
import {SelectTemplate} from '../../../invoice-management/invoices-list/invoice-edit-modal/itemSimple/steps/selectTemplate'
import {ProductPriceHistoryTable} from '../product-price-history/ProductPriceHistoryTable'
import {OnlinePriceHistoryTable} from '../product-price-history/OnlinePriceHistoryTable'
import {
  AutomaticInvoiceTemplateSettings,
  getAutomaticInvoiceTemplateSettingsIsInvalid,
  getAutomaticInvoiceTemplateSettingsPayload,
  normalizeAutomaticInvoiceTemplateSettings,
} from '../automatic-invoice-settings/AutomaticInvoiceTemplateSettings'
import {
  AutomaticInvoiceAdvancedSettings,
  getMinimumQuantityStepText,
  toPlainDecimalText,
} from '../automatic-invoice-settings/AutomaticInvoiceAdvancedSettings'
import {
  defaultPriceSourceOptions,
  getPriceSourceLabel,
  getPriceSourceOptions,
  manualPriceSourceOption,
} from '../product-price-source-options'
import {useAuth} from '../../../../auth'
import {isOnPremisesInstallation} from '../../../../auth/core/onPremisesAccess'
import {getProductTypeFromStuffId} from './product-type'
import {CatalogChoice} from '../../../shared/catalog-responsive/CatalogChoice'
import {readCatalogSaveError} from '../../../shared/catalog-responsive/catalogSaveError'
import {staffCan} from '../../../../auth/core/staffAccess'

const productTypeOptions = [
  {value: 1, label: 'کالا', icon: 'la-dolly'},
  {value: 2, label: 'خدمت', icon: 'la-hands-helping'},
]

const priceSourceModeOptions = [
  {value: 1, label: 'فقط قیمت دسته‌بندی', icon: 'la-layer-group'},
  {value: 2, label: 'قیمت کالا؛ در نبود آن دسته‌بندی', icon: 'la-tag'},
  {value: 3, label: 'جدیدترین قیمت معتبر', icon: 'la-clock'},
]

const PRODUCT_SAVE_FALLBACK_ERROR = 'ذخیره کالا/خدمت انجام نشد. دوباره تلاش کنید.'

type props = {
  productSelected?: {
    productSelected: Product | undefined
    setProductSelected: Dispatch<SetStateAction<Product | undefined>>
  }
  product?: Product
  invoiceDate: string
}

const getRatioNumber = (value: string | number | undefined | null) => {
  const ratio = Number(value || 0)
  return Number.isFinite(ratio) ? ratio : 0
}

const getRatioPreview = (ratio: number, total: number) => (
  <span className='fw-bold text-primary'>
    {numberWithCommas(ratio.toString())}/{numberWithCommas(total.toString())}
  </span>
)

const ProductEditModalForm: FC<props> = ({productSelected, product, invoiceDate}) => {
  const {isLoading, refetch} = useQueryResponse()
  const {setItemIdForUpdate} = useListView()
  const {currentUser} = useAuth()
  const isOnPremises = isOnPremisesInstallation(currentUser)
  const canManageAutomaticInvoice = staffCan(currentUser, 'automaticInvoice', 'manage') && !isOnPremises
  const [first, setFirst] = useState(true)
  const [type, setType] = useState(1)
  const [stuffid, setStuffid] = useState<string>('')
  const [stuffidInfo, setStuffidInfo] = useState<Stuffid | undefined | null>()
  const [code, setCode] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [link, setLink] = useState<string>('')
  const [priceSourceMode, setPriceSourceMode] = useState(2)
  const [priceSourceType, setPriceSourceType] = useState(1)
  const [useInAutomaticInvoice, setUseInAutomaticInvoice] = useState('0')
  const [automaticInvoiceRatio, setAutomaticInvoiceRatio] = useState('')
  const [automaticInvoiceQuantityDecimalPlaces, setAutomaticInvoiceQuantityDecimalPlaces] =
    useState('8')
  const [automaticInvoiceQuantityStep, setAutomaticInvoiceQuantityStep] = useState('0.00000001')
  const [automaticInvoiceMinQuantity, setAutomaticInvoiceMinQuantity] = useState('0')
  const [automaticInvoiceMaxQuantity, setAutomaticInvoiceMaxQuantity] = useState('')
  const [automaticInvoiceTemplateSettings, setAutomaticInvoiceTemplateSettings] = useState<
    AutomaticInvoiceTemplateSetting[]
  >([])
  const [productMaxLineDiscountPercent, setProductMaxLineDiscountPercent] = useState('5')
  const [automaticInvoiceRatioTotal, setAutomaticInvoiceRatioTotal] = useState(0)
  const [unit, setUnit] = useState<SearchSelectOption>()
  const [category, setCategory] = useState<SearchSelectOption>()
  const [categoryItems, setCategoryItems] = useState<SearchSelectOption[]>([])
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([])
  const [priceSourceOptions, setPriceSourceOptions] =
    useState<SearchSelectOption[]>(defaultPriceSourceOptions)
  const [submitting, setSubmitting] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [isActive, setIsActive] = useState('1')
  const [codeError, setCodeError] = useState<string>('')

  const checkCode = () => {
    setCodeError('')
    if (isNotEmpty(code)) {
      axios.post(`${API_URL}/products/checkCode`, {cd: code, id: product?.id || 0}).catch((err) => {
        setCodeError('کد وارد شده تکراری است.')
      })
    }
  }

  useEffect(() => {
    if (!isOnPremises) {
      getProductCategories().then((data) => {
        setProductCategories(data)
        setCategoryItems(
          data.map((item) => ({
            value: item.id,
            label: item.nm || '-',
          }))
        )
      })
    }
    if (canManageAutomaticInvoice) {
      getAutomaticInvoiceRatioSummary().then(setAutomaticInvoiceRatioTotal)
    }
    if (!isOnPremises) {
      getOnlineProductServicePriceSources()
        .then((sources) => setPriceSourceOptions(getPriceSourceOptions(sources)))
        .catch(() => setPriceSourceOptions(defaultPriceSourceOptions))
    }
  }, [canManageAutomaticInvoice, isOnPremises])

  useEffect(() => {
    if (first && product) {
      setStuffid(product.stin?.cd || '')
      setCode(product.cd || '')
      setLink(product.lnk || '')
      setDescription(product.nm || '')
      setPriceSourceMode(product.psm || 2)
      setPriceSourceType(product.pst || 1)
      setUseInAutomaticInvoice(product.aiv ? '1' : '0')
      setAutomaticInvoiceRatio((product.air ?? '').toString())
      setAutomaticInvoiceQuantityDecimalPlaces((product.aqd ?? 8).toString())
      setAutomaticInvoiceQuantityStep(toPlainDecimalText(product.qst ?? '0.00000001'))
      setAutomaticInvoiceMinQuantity((product.qmn ?? 0).toString())
      setAutomaticInvoiceMaxQuantity((product.qmx ?? '').toString())
      setAutomaticInvoiceTemplateSettings(
        normalizeAutomaticInvoiceTemplateSettings(product.aits, Boolean(product.aiv))
      )
      setProductMaxLineDiscountPercent((product.mld ?? 5).toString())
      setType(product.typ || 1)
      setIsActive(product.act === false ? '0' : '1')
      if (product.uni && product.un) {
        setUnit({
          value: product.uni,
          label: product.un,
        })
      }
      if (product.cid && product.cnm) {
        setCategory({
          value: product.cid,
          label: product.cnm,
        })
      }
      setFirst(false)
    }
  }, [product])

  const selectedCategoryId = !isOnPremises && category ? Number(category.value) : undefined
  const hasProductCategory = Boolean(selectedCategoryId)
  const selectedCategory =
    productCategories.find((item) => Number(item.id) === selectedCategoryId) ||
    (product?.cat && Number(product.cat.id) === selectedCategoryId ? product.cat : undefined)
  const categoryUsesAutomaticInvoice = Boolean(selectedCategory?.aiv)
  const categoryAutomaticInvoiceRatio = getRatioNumber(selectedCategory?.air)
  const categoryAutomaticInvoiceQuantityDecimalPlaces = Number(
    selectedCategory?.aqd ?? product?.cat?.aqd ?? 8
  )
  const categoryAutomaticInvoiceQuantityStep = toPlainDecimalText(
    selectedCategory?.qst ?? product?.cat?.qst ?? product?.eqst ?? '0.00000001'
  )
  const categoryAutomaticInvoiceMinQuantity =
    selectedCategory?.qmn ?? product?.cat?.qmn ?? product?.eqmn ?? 0
  const categoryAutomaticInvoiceMaxQuantity =
    selectedCategory?.qmx ?? product?.cat?.qmx ?? product?.eqmx ?? ''
  const categoryAutomaticInvoiceTemplateSettings = normalizeAutomaticInvoiceTemplateSettings(
    selectedCategory?.aits || product?.cat?.aits
  )
  const categoryMaxLineDiscountPercent = getRatioNumber(
    selectedCategory?.mld ?? product?.cat?.mld ?? 5
  )
  const productMaxLineDiscountPercentValue = Number(productMaxLineDiscountPercent)
  const automaticInvoiceQuantityDecimalPlacesValue = Number(automaticInvoiceQuantityDecimalPlaces)
  const automaticInvoiceQuantityStepValue = Number(automaticInvoiceQuantityStep)
  const automaticInvoiceMinQuantityValue = Number(automaticInvoiceMinQuantity)
  const automaticInvoiceMaxQuantityValue = Number(automaticInvoiceMaxQuantity)
  const minimumQuantityStepText = getMinimumQuantityStepText(
    automaticInvoiceQuantityDecimalPlacesValue
  )
  const minimumQuantityStep = Number(minimumQuantityStepText)
  const productUsesOwnAutomaticInvoice =
    canManageAutomaticInvoice && !hasProductCategory && useInAutomaticInvoice === '1'
  const productQuantityRangeIsEditable =
    canManageAutomaticInvoice && (hasProductCategory || productUsesOwnAutomaticInvoice)
  const productMaxLineDiscountPercentIsInvalid =
    productUsesOwnAutomaticInvoice &&
    (!Number.isFinite(productMaxLineDiscountPercentValue) ||
      productMaxLineDiscountPercentValue < 0 ||
      productMaxLineDiscountPercentValue > 99)
  const automaticInvoiceQuantityDecimalPlacesIsInvalid =
    productUsesOwnAutomaticInvoice &&
    (automaticInvoiceQuantityDecimalPlaces === '' ||
      !Number.isInteger(automaticInvoiceQuantityDecimalPlacesValue) ||
      automaticInvoiceQuantityDecimalPlacesValue < 0 ||
      automaticInvoiceQuantityDecimalPlacesValue > 8)
  const automaticInvoiceQuantityStepIsInvalid =
    productUsesOwnAutomaticInvoice &&
    (!Number.isFinite(automaticInvoiceQuantityStepValue) ||
      automaticInvoiceQuantityStepValue < minimumQuantityStep)
  const automaticInvoiceMinQuantityIsInvalid =
    productQuantityRangeIsEditable &&
    (!Number.isFinite(automaticInvoiceMinQuantityValue) || automaticInvoiceMinQuantityValue < 0)
  const automaticInvoiceMaxQuantityIsInvalid =
    productQuantityRangeIsEditable &&
    automaticInvoiceMaxQuantity !== '' &&
    (!Number.isFinite(automaticInvoiceMaxQuantityValue) ||
      automaticInvoiceMaxQuantityValue < 0 ||
      (automaticInvoiceMaxQuantityValue > 0 &&
        automaticInvoiceMaxQuantityValue < automaticInvoiceMinQuantityValue))
  const oldProductAutomaticInvoiceContribution =
    product?.eaiv && !product?.aii && !product?.cid ? getRatioNumber(product.eair) : 0
  const productAutomaticInvoiceContribution = productUsesOwnAutomaticInvoice
    ? getRatioNumber(automaticInvoiceRatio)
    : 0
  const automaticInvoiceTotalPreview = Math.max(
    0,
    automaticInvoiceRatioTotal -
      oldProductAutomaticInvoiceContribution +
      productAutomaticInvoiceContribution
  )
  const inheritedAutomaticInvoiceTotalPreview = Math.max(
    0,
    automaticInvoiceRatioTotal - oldProductAutomaticInvoiceContribution
  )
  const automaticInvoiceHint =
    hasProductCategory && categoryUsesAutomaticInvoice
      ? getRatioPreview(categoryAutomaticInvoiceRatio, inheritedAutomaticInvoiceTotalPreview)
      : getRatioPreview(productAutomaticInvoiceContribution, automaticInvoiceTotalPreview)
  const automaticInvoiceRatioIsInvalid =
    productUsesOwnAutomaticInvoice && productAutomaticInvoiceContribution <= 0
  const automaticInvoiceTemplateSettingsIsInvalid =
    productUsesOwnAutomaticInvoice &&
    getAutomaticInvoiceTemplateSettingsIsInvalid(
      automaticInvoiceTemplateSettings,
      productUsesOwnAutomaticInvoice
    )
  const selectedPriceSourceType = Number(priceSourceType || 1)
  const selectedPriceSourceOption =
    priceSourceOptions.find((item) => Number(item.value) === selectedPriceSourceType) ||
    (selectedPriceSourceType > 1
      ? {
          value: selectedPriceSourceType,
          label: `منبع قیمت ${selectedPriceSourceType}`,
        }
      : manualPriceSourceOption)
  const setSelectedPriceSourceOption: Dispatch<SetStateAction<SearchSelectOption | undefined>> = (
    nextValue
  ) => {
    const next = typeof nextValue === 'function' ? nextValue(selectedPriceSourceOption) : nextValue
    setPriceSourceType(Number(next?.value || 1))
  }

  useEffect(() => {
    if (productUsesOwnAutomaticInvoice && automaticInvoiceTemplateSettings.length === 0) {
      setAutomaticInvoiceTemplateSettings(normalizeAutomaticInvoiceTemplateSettings([], true))
    }
  }, [productUsesOwnAutomaticInvoice, automaticInvoiceTemplateSettings.length])

  const saveStuffId = async () => {
    if (
      automaticInvoiceRatioIsInvalid ||
      automaticInvoiceQuantityDecimalPlacesIsInvalid ||
      automaticInvoiceQuantityStepIsInvalid ||
      automaticInvoiceMinQuantityIsInvalid ||
      automaticInvoiceMaxQuantityIsInvalid ||
      automaticInvoiceTemplateSettingsIsInvalid ||
      productMaxLineDiscountPercentIsInvalid
    ) {
      return
    }

    setSubmitting(true)
    setSaveError('')

    try {
      const response = await updateProduct({
        id: product ? product.id : 0,
        cd: code,
        nm: description,
        lnk: link,
        psm: isOnPremises ? product?.psm ?? 2 : hasProductCategory ? priceSourceMode : 2,
        pst: isOnPremises ? product?.pst ?? 1 : selectedPriceSourceType,
        aiv: isOnPremises ? Boolean(product?.aiv) : productUsesOwnAutomaticInvoice,
        air: isOnPremises
          ? product?.air ?? 0
          : productUsesOwnAutomaticInvoice
          ? automaticInvoiceRatio
          : 0,
        aqd: isOnPremises ? product?.aqd ?? 8 : Number(automaticInvoiceQuantityDecimalPlaces || 8),
        qst: isOnPremises
          ? product?.qst ?? 0.00000001
          : Number(automaticInvoiceQuantityStep || minimumQuantityStepText),
        qmn: isOnPremises ? product?.qmn ?? 0 : Number(automaticInvoiceMinQuantity || 0),
        qmx: isOnPremises
          ? product?.qmx ?? 0
          : automaticInvoiceMaxQuantity === ''
          ? 0
          : Number(automaticInvoiceMaxQuantity),
        aits: isOnPremises
          ? product?.aits ?? []
          : getAutomaticInvoiceTemplateSettingsPayload(
              automaticInvoiceTemplateSettings,
              productUsesOwnAutomaticInvoice
            ),
        mld: isOnPremises
          ? product?.mld ?? 0
          : productMaxLineDiscountPercent === ''
          ? 0
          : productMaxLineDiscountPercent,
        sti: stuffid,
        uni: unit && Number(unit.value),
        cid: isOnPremises ? product?.cid ?? 0 : category ? Number(category.value) : 0,
        typ: type,
        act: isActive == '1',
      })
      if (productSelected && response) {
        productSelected.setProductSelected(response)
      }
    } catch (error: unknown) {
      // The dialog stays open with what was typed; the reason sits beside «ذخیره» (507: an alert() showed raw English
      // and the dialog closed on a failure too).
      setSaveError(readCatalogSaveError(error, PRODUCT_SAVE_FALLBACK_ERROR))
      setSubmitting(false)
      return
    }

    refetch()
    window.dispatchEvent(new Event('megapay-product-ratio-changed'))
    setItemIdForUpdate(undefined)
  }

  useEffect(() => {
    if (stuffidInfo != null && stuffidInfo != undefined) {
      const stuffIdProductType = getProductTypeFromStuffId(stuffidInfo)
      if (stuffIdProductType) {
        setType(stuffIdProductType)
      }
      if (!isNotEmpty(description)) {
        setDescription(stuffidInfo.ds || '')
      }
      setStuffid(stuffidInfo.cd || '')
    }
  }, [stuffidInfo])

  return (
    <>
      {!isNotEmpty(stuffid) ? (
        <SelectTemplate
          stuffidSelected={{
            stuffidInfo: stuffidInfo,
            setStuffidInfo: setStuffidInfo,
          }}
        />
      ) : (
        <>
          <div className='row'>
            <div className='col-12 my-3'>
              <ProductStuffIdEdit
                isRequired={false}
                stuffidState={{stuffid, setStuffid}}
                stuffidInfoState={{stuffidInfo, setStuffidInfo}}
                isDisabled={submitting}
                label={'شناسه مالیاتی کالا/خدمت در سامانه مؤدیان'}
                error={
                  !isNotEmpty(stuffid) && (
                    <span className='fs-7 text-gray-500'>
                      در صورت عدم درج شناسه کالا/خدمت، امکان ارسال صورتحساب شامل این کالا/خدمت به
                      سامانه مؤدیان وجود نخواهد داشت.
                    </span>
                  )
                }
              />
            </div>
            <div className='col-12'>
              <div className='separator my-5'></div>
            </div>
            <div className='col-lg-6 col-md-12 my-3'>
              {/* A segmented choice (507): the radios sat apart from their labels. The goods ID decides it when known. */}
              <CatalogChoice
                legend='نوع'
                isRequired
                isDisabled={submitting || (stuffidInfo != null && stuffidInfo != undefined)}
                options={productTypeOptions}
                value={type}
                onChange={setType}
                hint={
                  stuffidInfo ? (
                    <span className='fs-7'>نوع از شناسه کالا/خدمت خوانده می‌شود.</span>
                  ) : undefined
                }
              />
            </div>
            {product?.id && (
              <div className='col-lg-6 col-md-12 my-3'>
                <CustomInputs
                  type='toggle'
                  stateValue={{
                    value: isActive,
                    setValue: setIsActive,
                  }}
                  label={`وضعیت ${type == 1 ? 'کالا' : 'خدمت'}`}
                  isDisabled={submitting}
                  toggleValue={{
                    on: 'فعال',
                    off: 'آرشیو (حذف از جدول)',
                  }}
                  hint={
                    isActive === '0' ? (
                      <span className='fs-7 text-warning'>
                        این کالا/خدمت از جدول حذف می‌شود اما سوابق قبلی آن باقی می‌ماند.
                      </span>
                    ) : undefined
                  }
                />
              </div>
            )}
            <div className='col-12 my-3 d-none'>
              <div className='mp-panel'>
                <div className='row align-items-end g-4'>
                  <div className='col-lg-4 col-md-6'>
                    <CustomInputs
                      type='toggle'
                      label='استفاده در صورتحساب خودکار'
                      isDisabled={submitting || categoryUsesAutomaticInvoice}
                      stateValue={{
                        value: categoryUsesAutomaticInvoice ? '1' : useInAutomaticInvoice,
                        setValue: setUseInAutomaticInvoice,
                      }}
                      toggleValue={{
                        off: 'استفاده نشود',
                        on: 'استفاده شود',
                      }}
                      hint={
                        categoryUsesAutomaticInvoice ? (
                          <span className='fs-7 text-info'>
                            این گزینه از دسته‌بندی {selectedCategory?.nm} خوانده می‌شود و در کالا
                            قابل تغییر نیست.
                          </span>
                        ) : undefined
                      }
                    />
                  </div>
                  {(categoryUsesAutomaticInvoice || useInAutomaticInvoice === '1') && (
                    <div className='col-lg-3 col-md-6'>
                      <CustomInputs
                        type='number'
                        numberAsText
                        label='عدد تناسب'
                        isRequired={!categoryUsesAutomaticInvoice}
                        isDisabled={submitting || categoryUsesAutomaticInvoice}
                        stateValue={{
                          value: categoryUsesAutomaticInvoice
                            ? categoryAutomaticInvoiceRatio
                            : automaticInvoiceRatio,
                          setValue: setAutomaticInvoiceRatio,
                        }}
                        hint={
                          <span className='fs-7 text-gray-600'>
                            نسبت در جمع کل: {automaticInvoiceHint}
                          </span>
                        }
                        error={
                          automaticInvoiceRatioIsInvalid ? (
                            <span className='fs-7 text-danger'>
                              عدد تناسب باید بزرگ‌تر از صفر باشد.
                            </span>
                          ) : undefined
                        }
                      />
                    </div>
                  )}
                  {(categoryUsesAutomaticInvoice || useInAutomaticInvoice === '1') && (
                    <div className='col-lg-2 col-md-6'>
                      {selectedCategory ? (
                        <div className='rounded border border-dashed border-warning bg-light-warning px-4 py-3'>
                          <div className='fs-8 text-gray-600 mb-1'>حداکثر تخفیف</div>
                          <div className='fs-5 fw-bold text-warning'>
                            {numberWithCommas(categoryMaxLineDiscountPercent.toString())}%
                          </div>
                        </div>
                      ) : (
                        <CustomInputs
                          type='number'
                          numberAsText
                          label='حداکثر تخفیف (%)'
                          isDisabled={submitting}
                          stateValue={{
                            value: productMaxLineDiscountPercent,
                            setValue: setProductMaxLineDiscountPercent,
                          }}
                          error={
                            productMaxLineDiscountPercentIsInvalid ? (
                              <span className='fs-7 text-danger'>
                                درصد تخفیف باید بین ۰ تا ۹۹ باشد.
                              </span>
                            ) : undefined
                          }
                        />
                      )}
                    </div>
                  )}
                  <div
                    className={
                      categoryUsesAutomaticInvoice || useInAutomaticInvoice === '1'
                        ? 'col-lg-3 col-md-6'
                        : 'col-lg-3 col-md-12'
                    }
                  >
                    <div className='rounded border border-dashed border-primary bg-light-primary px-4 py-3'>
                      <div className='fs-8 text-gray-600 mb-1'>نسبت نهایی</div>
                      <div className='fs-5'>{automaticInvoiceHint}</div>
                    </div>
                  </div>
                  {categoryUsesAutomaticInvoice && (
                    <div className='col-12'>
                      <AutomaticInvoiceTemplateSettings
                        value={categoryAutomaticInvoiceTemplateSettings}
                        readOnly
                        disabled={submitting}
                        error={categoryAutomaticInvoiceTemplateSettings.length === 0}
                      />
                      {categoryAutomaticInvoiceTemplateSettings.length === 0 && (
                        <div className='fs-7 text-danger mt-2'>
                          دسته‌بندی انتخاب‌شده هنوز الگوی صورتحساب خودکار ندارد.
                        </div>
                      )}
                    </div>
                  )}
                  {!categoryUsesAutomaticInvoice && useInAutomaticInvoice === '1' && (
                    <div className='col-12'>
                      <AutomaticInvoiceTemplateSettings
                        value={automaticInvoiceTemplateSettings}
                        onChange={setAutomaticInvoiceTemplateSettings}
                        disabled={submitting}
                        error={automaticInvoiceTemplateSettingsIsInvalid}
                      />
                      {automaticInvoiceTemplateSettingsIsInvalid && (
                        <div className='fs-7 text-danger mt-2'>
                          حداقل یک الگوی صورتحساب انتخاب کنید؛ درصدهای واردشده برای الگوی طلا باید
                          بین ۰ تا ۱۰۰ باشند و فیلد خالی صفر در نظر گرفته می‌شود.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='col-lg-6 col-md-12 my-3'>
              <CustomInputs
                type='text'
                label={`عنوان ${type == 1 ? 'کالا' : 'خدمت'} در صورتحساب`}
                isRequired
                isDisabled={submitting}
                stateValue={{value: description, setValue: setDescription}}
              />
            </div>
            <div className='col-lg-6 col-md-12 my-3'>
              <CustomInputs
                type='text'
                label={`کد داخلی ${type == 1 ? 'کالا' : 'خدمت'} در کسب‌وکار`}
                isDisabled={submitting}
                error={<span className='fs-7 text-danger'>{codeError}</span>}
                hint={
                  <span className='fs-7 text-gray-500'>
                    این کد، کد اختصاصی شما برای کالای/خدمت‌تان در کسب‌وکارتان است. (جهت جستجوی آسان
                    در صدور صورتحساب)
                  </span>
                }
                onBlurFn={checkCode}
                stateValue={{value: code, setValue: setCode}}
              />
            </div>
            <div className='col-lg-6 col-md-12 my-3'>
              <SearchSelect
                section='unit'
                label='واحد اندازه‌گیری'
                isDisabled={submitting}
                stateValue={{
                  value: unit,
                  setValue: setUnit,
                }}
                hint={
                  <>
                    {unit && unit.label?.toString().startsWith('*') && (
                      <span className='fs-7 text-info'>
                        این واحد اندازه‌گیری در سامانه مؤدیان تعریف نشده است. با انتخاب این واحد،
                        کالا/خدمت شما بدون واحد به سامانه مؤدیان ارسال می‌شود، اما در صورتحساب چاپی
                        {projectName}، نمایش داده خواهد شد.
                      </span>
                    )}
                    {!unit && (
                      <a target='_blank' href='#' className='fs-7 text-gray-500'>
                        اگر واحد اندازه‌گیری مورد نظرتان در لیست موجود نیست، اینجا کلیک کنید.
                      </a>
                    )}
                  </>
                }
              />
            </div>
            {!isOnPremises && (
              <div className='col-lg-6 col-md-12 my-3'>
                <SearchSelect
                  section='productCategory'
                  label={
                    canManageAutomaticInvoice ? 'دسته‌بندی قیمت و صدور خودکار' : 'دسته‌بندی قیمت'
                  }
                  placeholder='بدون دسته‌بندی'
                  isDisabled={submitting}
                  items={categoryItems}
                  stateValue={{
                    value: category,
                    setValue: setCategory,
                  }}
                />
              </div>
            )}
            <div className='col-lg-6 col-md-12 my-3'>
              <CustomInputs
                type='text'
                label={`لینک معرفی ${type == 1 ? 'کالا' : 'خدمت'} در صورتحساب الکترونیکی`}
                hint={
                  <span className='fs-7 text-gray-500'>
                    در این فیلد، می‌توانید لینکی جهت معرفی کامل‌تر کالا/خدمت خود به مشتریان خود وارد
                    کنید. هنگام صدور صورتحساب الکترونیکی در {projectName}، این لینک به صورت
                    هایپرلینک درج خواهد شد و مشتریان شما با کلیک بر روی آن، می‌توانند به اطلاعات
                    بیشتر در مورد {type == 1 ? 'کالا' : 'خدمت'} شما دسترسی پیدا کنند.
                  </span>
                }
                stateValue={{value: link, setValue: setLink}}
                LTR
                isDisabled={submitting}
              />
            </div>
            <div className='col-12 my-3'>
              <div className='mp-panel'>
                {hasProductCategory && (
                  <>
                    <CatalogChoice
                      legend='منطق انتخاب قیمت هنگام صدور'
                      layout='stack'
                      isDisabled={submitting}
                      options={priceSourceModeOptions}
                      value={priceSourceMode}
                      onChange={setPriceSourceMode}
                    />
                    <div className='separator my-5'></div>
                  </>
                )}
                {canManageAutomaticInvoice && (
                  <div className='mp-auto-invoice-settings'>
                    {hasProductCategory ? (
                      <div className='rounded border border-dashed border-info bg-light-info px-4 py-4'>
                        <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3'>
                          <div>
                            <div className='fw-bold text-gray-900'>تنظیمات صورتحساب خودکار</div>
                            <div className='fs-7 text-gray-600'>
                              طبق دسته‌بندی {selectedCategory?.nm || category?.label || ''}
                            </div>
                          </div>
                          <span
                            className={
                              categoryUsesAutomaticInvoice
                                ? 'badge badge-light-success'
                                : 'badge badge-light'
                            }
                          >
                            {categoryUsesAutomaticInvoice ? 'فعال' : 'غیرفعال'}
                          </span>
                        </div>
                        {categoryUsesAutomaticInvoice ? (
                          <>
                            <div className='d-flex align-items-center flex-wrap gap-2 mb-3'>
                              <span className='badge badge-light-primary'>
                                نسبت: {automaticInvoiceHint}
                              </span>
                              <span className='badge badge-light-info d-none'>
                                اعشار مقدار: {categoryAutomaticInvoiceQuantityDecimalPlaces}
                              </span>
                              <span className='badge badge-light-warning'>
                                حداکثر تخفیف:{' '}
                                {numberWithCommas(categoryMaxLineDiscountPercent.toString())}%
                              </span>
                            </div>
                            <div className='mb-3'>
                              <AutomaticInvoiceAdvancedSettings
                                readOnly
                                title='تنظیمات پیشرفته دسته‌بندی'
                                quantityDecimalPlaces={
                                  categoryAutomaticInvoiceQuantityDecimalPlaces
                                }
                                quantityStep={categoryAutomaticInvoiceQuantityStep}
                                minQuantity={categoryAutomaticInvoiceMinQuantity}
                                maxQuantity={categoryAutomaticInvoiceMaxQuantity || ''}
                              />
                            </div>
                            <AutomaticInvoiceTemplateSettings
                              value={categoryAutomaticInvoiceTemplateSettings}
                              readOnly
                              compactReadOnly
                              disabled={submitting}
                              error={categoryAutomaticInvoiceTemplateSettings.length === 0}
                            />
                          </>
                        ) : (
                          <div className='fs-7 text-gray-600'>
                            برای این دسته‌بندی استفاده در صورتحساب خودکار فعال نیست.
                          </div>
                        )}
                        <div className='mt-3'>
                          <AutomaticInvoiceAdvancedSettings
                            title='محدوده مقدار این کالا/خدمت'
                            disabled={submitting}
                            showQuantityDecimalPlaces={false}
                            showQuantityStep={false}
                            quantityDecimalPlaces={categoryAutomaticInvoiceQuantityDecimalPlaces}
                            quantityStep={categoryAutomaticInvoiceQuantityStep}
                            minQuantity={automaticInvoiceMinQuantity}
                            setMinQuantity={setAutomaticInvoiceMinQuantity}
                            maxQuantity={automaticInvoiceMaxQuantity}
                            setMaxQuantity={setAutomaticInvoiceMaxQuantity}
                            errors={{
                              minQuantity: automaticInvoiceMinQuantityIsInvalid ? (
                                <span className='fs-7 text-danger'>
                                  حداقل مقدار نمی‌تواند منفی باشد.
                                </span>
                              ) : undefined,
                              maxQuantity: automaticInvoiceMaxQuantityIsInvalid ? (
                                <span className='fs-7 text-danger'>
                                  حداکثر مقدار باید خالی، ۰، یا بزرگ‌تر از حداقل مقدار باشد.
                                </span>
                              ) : undefined,
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className='rounded border border-dashed border-primary bg-light-primary px-4 py-4'>
                        <div className='row align-items-end g-4'>
                          <div className='col-lg-3 col-md-6'>
                            <CustomInputs
                              type='toggle'
                              label='استفاده در صورتحساب خودکار'
                              isDisabled={submitting}
                              stateValue={{
                                value: useInAutomaticInvoice,
                                setValue: setUseInAutomaticInvoice,
                              }}
                              toggleValue={{
                                off: 'استفاده نشود',
                                on: 'استفاده شود',
                              }}
                            />
                          </div>
                          {productUsesOwnAutomaticInvoice && (
                            <>
                              <div className='col-lg-2 col-md-6'>
                                <CustomInputs
                                  type='number'
                                  numberAsText
                                  label='عدد تناسب'
                                  isRequired
                                  isDisabled={submitting}
                                  stateValue={{
                                    value: automaticInvoiceRatio,
                                    setValue: setAutomaticInvoiceRatio,
                                  }}
                                  hint={
                                    <span className='fs-7 text-gray-600'>
                                      نسبت در جمع کل: {automaticInvoiceHint}
                                    </span>
                                  }
                                  error={
                                    automaticInvoiceRatioIsInvalid ? (
                                      <span className='fs-7 text-danger'>
                                        عدد تناسب باید بزرگ‌تر از صفر باشد.
                                      </span>
                                    ) : undefined
                                  }
                                />
                              </div>
                              <div className='col-lg-2 col-md-6 d-none'>
                                <CustomInputs
                                  type='number'
                                  numberAsText
                                  label='اعشار مجاز مقدار'
                                  isRequired
                                  isDisabled={submitting}
                                  stateValue={{
                                    value: automaticInvoiceQuantityDecimalPlaces,
                                    setValue: setAutomaticInvoiceQuantityDecimalPlaces,
                                  }}
                                  hint={<span className='fs-7 text-gray-600'>حداکثر ۸ رقم</span>}
                                  error={
                                    automaticInvoiceQuantityDecimalPlacesIsInvalid ? (
                                      <span className='fs-7 text-danger'>
                                        تعداد اعشار باید عدد صحیح بین ۰ تا ۸ باشد.
                                      </span>
                                    ) : undefined
                                  }
                                />
                              </div>
                              <div className='col-lg-2 col-md-6'>
                                <CustomInputs
                                  type='number'
                                  numberAsText
                                  label='حداکثر تخفیف (%)'
                                  isDisabled={submitting}
                                  stateValue={{
                                    value: productMaxLineDiscountPercent,
                                    setValue: setProductMaxLineDiscountPercent,
                                  }}
                                  error={
                                    productMaxLineDiscountPercentIsInvalid ? (
                                      <span className='fs-7 text-danger'>
                                        درصد تخفیف باید بین ۰ تا ۹۹ باشد.
                                      </span>
                                    ) : undefined
                                  }
                                />
                              </div>
                              <div className='col-lg-3 col-md-6'>
                                <div className='rounded border border-dashed border-primary bg-white px-4 py-3'>
                                  <div className='fs-8 text-gray-600 mb-1'>نسبت نهایی</div>
                                  <div className='fs-5'>{automaticInvoiceHint}</div>
                                </div>
                              </div>
                              <div className='col-12'>
                                <AutomaticInvoiceAdvancedSettings
                                  disabled={submitting}
                                  quantityDecimalPlaces={automaticInvoiceQuantityDecimalPlaces}
                                  setQuantityDecimalPlaces={
                                    setAutomaticInvoiceQuantityDecimalPlaces
                                  }
                                  quantityStep={automaticInvoiceQuantityStep}
                                  setQuantityStep={setAutomaticInvoiceQuantityStep}
                                  minQuantity={automaticInvoiceMinQuantity}
                                  setMinQuantity={setAutomaticInvoiceMinQuantity}
                                  maxQuantity={automaticInvoiceMaxQuantity}
                                  setMaxQuantity={setAutomaticInvoiceMaxQuantity}
                                  errors={{
                                    quantityDecimalPlaces:
                                      automaticInvoiceQuantityDecimalPlacesIsInvalid ? (
                                        <span className='fs-7 text-danger'>
                                          تعداد اعشار باید عدد صحیح بین ۰ تا ۸ باشد.
                                        </span>
                                      ) : undefined,
                                    quantityStep: automaticInvoiceQuantityStepIsInvalid ? (
                                      <span className='fs-7 text-danger'>
                                        گام تغییر مقدار باید حداقل {minimumQuantityStepText} باشد.
                                      </span>
                                    ) : undefined,
                                    minQuantity: automaticInvoiceMinQuantityIsInvalid ? (
                                      <span className='fs-7 text-danger'>
                                        حداقل مقدار نمی‌تواند منفی باشد.
                                      </span>
                                    ) : undefined,
                                    maxQuantity: automaticInvoiceMaxQuantityIsInvalid ? (
                                      <span className='fs-7 text-danger'>
                                        حداکثر مقدار باید خالی، ۰، یا بزرگ‌تر از حداقل مقدار باشد.
                                      </span>
                                    ) : undefined,
                                  }}
                                />
                              </div>
                              <div className='col-12'>
                                <AutomaticInvoiceTemplateSettings
                                  value={automaticInvoiceTemplateSettings}
                                  onChange={setAutomaticInvoiceTemplateSettings}
                                  disabled={submitting}
                                  error={automaticInvoiceTemplateSettingsIsInvalid}
                                />
                                {automaticInvoiceTemplateSettingsIsInvalid && (
                                  <div className='fs-7 text-danger mt-2'>
                                    حداقل یک الگوی صورتحساب انتخاب کنید؛ درصدهای واردشده برای الگوی
                                    طلا باید بین ۰ تا ۱۰۰ باشند و فیلد خالی صفر در نظر گرفته می‌شود.
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className='separator my-5'></div>
                {!isOnPremises && (
                  <div className='row g-4 align-items-end mb-4'>
                    <div className='col-lg-5 col-md-7'>
                      <SearchSelect
                        section='productPriceSource'
                        label='منبع قیمت خود کالا/خدمت'
                        isDisabled={submitting}
                        stateValue={{
                          value: selectedPriceSourceOption,
                          setValue: setSelectedPriceSourceOption,
                        }}
                        items={priceSourceOptions}
                        hint={
                          selectedPriceSourceType === 1 ? (
                            <span className='fs-7 text-gray-600'>
                              قیمت این کالا/خدمت از جدول قیمت دستی خودش خوانده می‌شود.
                            </span>
                          ) : (
                            <span className='fs-7 text-info'>
                              قیمت این کالا/خدمت از سرویس آنلاین{' '}
                              {getPriceSourceLabel(selectedPriceSourceType, priceSourceOptions)}{' '}
                              خوانده می‌شود.
                            </span>
                          )
                        }
                      />
                    </div>
                    {hasProductCategory && (
                      <div className='col-lg-7 col-md-5'>
                        <div className='rounded border border-dashed border-info bg-light-info px-4 py-3'>
                          <div className='fw-bold text-info mb-1'>منطق ترکیب با دسته‌بندی</div>
                          <div className='fs-7 text-gray-700'>
                            گزینه بالایی مشخص می‌کند قیمت خود کالا دستی است یا آنلاین؛ مدل انتخاب
                            قیمت هم تعیین می‌کند هنگام صدور از قیمت کالا، دسته‌بندی یا جدیدترین قیمت
                            معتبر استفاده شود.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {isOnPremises || selectedPriceSourceType === 1 ? (
                  <ProductPriceHistoryTable
                    productServiceId={product?.id}
                    title='جدول قیمت دستی کالا/خدمت'
                    onChanged={refetch}
                  />
                ) : (
                  <OnlinePriceHistoryTable
                    priceSourceType={selectedPriceSourceType}
                    title={`جدول قیمت ${getPriceSourceLabel(
                      selectedPriceSourceType,
                      priceSourceOptions
                    )}`}
                  />
                )}
              </div>
            </div>
          </div>
          <div className='row mt-5 mp-catalog-form-actions'>
            <div className='col-12 text-end'>
              {saveError && (
                <div className='alert alert-danger d-flex align-items-start gap-2 py-3' role='alert'>
                  <i className='las la-exclamation-circle fs-2 text-danger' aria-hidden='true'></i>
                  <span>{saveError}</span>
                </div>
              )}
              <button
                type='button'
                className='btn btn-success'
                onClick={saveStuffId}
                disabled={
                  !isNotEmpty(description) ||
                  submitting ||
                  (isNotEmpty(stuffid) && !stuffidInfo) ||
                  automaticInvoiceRatioIsInvalid ||
                  automaticInvoiceQuantityDecimalPlacesIsInvalid ||
                  automaticInvoiceQuantityStepIsInvalid ||
                  automaticInvoiceMinQuantityIsInvalid ||
                  automaticInvoiceMaxQuantityIsInvalid ||
                  automaticInvoiceTemplateSettingsIsInvalid ||
                  productMaxLineDiscountPercentIsInvalid ||
                  isNotEmpty(codeError)
                }
              >
                ذخیره
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export {ProductEditModalForm}
